/**
 * <s-wishlist>
 * Body of the wishlist drawer. Reads the saved handles (see s-wish-button.js),
 * loads each product from /products/{handle}.js and renders a row in the cart
 * style: keyed image, title, price, "Ansehen" and remove. Also keeps every
 * [data-wishlist-count] on the page (header badge, drawer title) in sync.
 * Products that no longer exist are dropped from the list.
 *
 * data-style (theme settings › Warenkorb › Stil der Merkliste):
 * - original / look: rows as above (look is pure CSS, snippets/cart-drawer.liquid).
 * - quick: each row also gets size pills + "In den Warenkorb" (Shopify.actions.updateCart).
 * - bar: quick + the pinned drawer footer [data-wishlist-bar] with the list total and "Zum Warenkorb".
 */
import { readWishlist, writeWishlist } from './s-wish-button.js';

class SWishlist extends HTMLElement {
  #ac = null;
  #cache = new Map();
  #token = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    document.addEventListener('wishlist:change', () => this.#render(), { signal });
    window.addEventListener('storage', (event) => {
      if (event.key === 'ma-wishlist') this.#render();
    }, { signal });
    window.addEventListener('hashchange', () => this.#openFromLink(), { signal });
    this.#bar()?.querySelector('[data-wishlist-to-cart]')?.addEventListener('click', () => this.#goToCart(), { signal });
    // Any link to #merkliste opens the drawer on the current page. Links can sit inside Shopify's account popover (shadow DOM), so read the composed path.
    document.addEventListener('click', (event) => {
      const link = event.composedPath().find((el) => el instanceof HTMLAnchorElement && el.href);
      if (!link) return;
      const url = new URL(link.href, window.location.href);
      if (url.hash !== '#merkliste') return;
      event.preventDefault();
      this.#open();
    }, { signal, capture: true });
    // Wait until the surrounding drawer and its focus-management wrapper are connected.
    requestAnimationFrame(() => {
      if (this.isConnected) this.#openFromLink();
    });
    this.#render();
  }

  #openFromLink() {
    if (window.location.hash !== '#merkliste') return;
    this.#open();
    // Consume the action so the same menu link can open the drawer again after closing.
    const url = new URL(window.location.href);
    url.hash = '';
    window.history.replaceState(window.history.state, '', url);
  }

  #open() {
    const dialog = this.closest('dialog');
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) return;
    // Let the account popover close first so the drawer lands on top.
    setTimeout(() => dialog.showModal(), 60);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #updateCounts(count) {
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  }

  async #load(handle) {
    if (this.#cache.has(handle)) return this.#cache.get(handle);
    const root = (this.dataset.root || '/').replace(/\/?$/, '/');
    const promise = fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);
    this.#cache.set(handle, promise);
    return promise;
  }

  #money(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  async #render() {
    const token = ++this.#token;
    const handles = readWishlist();
    this.#updateCounts(handles.length);

    const list = this.querySelector('[data-wishlist-items]');
    const empty = this.querySelector('[data-wishlist-empty]');
    if (!list || !empty) return;
    empty.hidden = handles.length > 0;
    if (!handles.length) {
      list.replaceChildren();
      this.#updateBar([]);
      return;
    }

    const products = await Promise.all(handles.map((handle) => this.#load(handle)));
    if (token !== this.#token) return;

    const missing = handles.filter((_, i) => !products[i]);
    if (missing.length) {
      writeWishlist(handles.filter((h) => !missing.includes(h)));
      return;
    }

    list.replaceChildren(...products.map((product) => this.#row(product)));
    this.#updateBar(products);
  }

  get #quick() {
    return this.dataset.style === 'quick' || this.dataset.style === 'bar';
  }

  #bar() {
    return this.closest('dialog')?.querySelector('[data-wishlist-bar]') ?? null;
  }

  #updateBar(products) {
    const bar = this.#bar();
    if (!bar) return;
    bar.hidden = products.length === 0;
    const total = bar.querySelector('[data-wishlist-total]');
    if (total) total.textContent = this.#money(products.reduce((sum, p) => sum + (Number(p.price) || 0), 0));
  }

  async #goToCart() {
    this.closest('dialog')?.close();
    const cart = document.getElementById('cart-drawer');
    if (cart && typeof cart.showModal === 'function' && !cart.open) {
      setTimeout(() => cart.showModal(), 60);
    } else {
      await window.Shopify?.actions?.openCart?.();
    }
  }

  /** Size pills + add button for quick / bar. Each product is one colour, so a variant = a size. */
  #setupQuickAdd(row, product) {
    const sizes = row.querySelector('[data-row-sizes]');
    const add = row.querySelector('[data-row-add]');
    const error = row.querySelector('[data-row-error]');
    const price = row.querySelector('[data-row-price]');
    if (!sizes || !add || !product.available) return;

    const variants = Array.isArray(product.variants) ? product.variants : [];
    let selected = null;
    const select = (variant) => {
      selected = variant;
      add.disabled = false;
      add.textContent = this.dataset.labelAdd || '';
      if (error) error.hidden = true;
      if (price) price.textContent = this.#money(variant.price);
      sizes.querySelectorAll('[data-row-size]').forEach((pill) => {
        pill.setAttribute('aria-pressed', String(pill.dataset.variantId === String(variant.id)));
      });
    };

    add.hidden = false;
    if (variants.length <= 1) {
      if (variants[0]) select(variants[0]);
    } else {
      sizes.hidden = false;
      add.disabled = true;
      add.textContent = this.dataset.labelPick || '';
      for (const variant of variants) {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.dataset.rowSize = '';
        pill.dataset.variantId = String(variant.id);
        pill.textContent = variant.public_title || variant.title || '';
        pill.setAttribute('aria-pressed', 'false');
        if (!variant.available) {
          pill.disabled = true;
          pill.setAttribute('aria-label', `${pill.textContent} (${this.dataset.labelSoldOut || ''})`);
        }
        pill.addEventListener('click', () => select(variant));
        sizes.append(pill);
      }
    }

    add.addEventListener('click', async () => {
      if (!selected || !window.Shopify?.actions?.updateCart) return;
      add.setAttribute('aria-busy', 'true');
      add.disabled = true;
      if (error) error.hidden = true;
      try {
        const result = await window.Shopify.actions.updateCart(
          { lines: [{ merchandiseId: String(selected.id), quantity: 1 }] },
          { event: { context: 'product', detail: { source: 's-wishlist' } } }
        );
        const message = result?.userErrors?.[0]?.message;
        if (message) throw new Error(message);
        add.textContent = `${this.dataset.labelAdded || ''} ✓`;
        add.dataset.added = '';
        setTimeout(() => {
          delete add.dataset.added;
          add.textContent = this.dataset.labelAdd || '';
          add.disabled = false;
        }, 2200);
      } catch (err) {
        add.disabled = false;
        if (error) {
          error.textContent = err?.message || this.dataset.labelError || '';
          error.hidden = false;
        }
      } finally {
        add.removeAttribute('aria-busy');
      }
    });
  }

  #row(product) {
    const template = this.querySelector('template[data-wishlist-row]');
    const row = template.content.firstElementChild.cloneNode(true);
    const url = product.url || `/products/${product.handle}`;

    row.querySelectorAll('[data-row-link]').forEach((a) => {
      a.href = url;
    });
    const img = row.querySelector('img');
    if (img && product.featured_image) {
      const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
      const sized = new URL(src, location.href);
      sized.searchParams.set('width', '400');
      img.src = sized.toString();
      img.alt = product.title;
    } else if (img) {
      img.remove();
    }
    row.querySelector('[data-row-title]').textContent = product.title;
    row.querySelector('[data-row-price]').textContent = this.#money(product.price);
    if (!product.available) row.querySelector('[data-row-soldout]').hidden = false;
    if (this.#quick) this.#setupQuickAdd(row, product);

    const remove = row.querySelector('[data-row-remove]');
    remove.setAttribute('aria-label', `${remove.getAttribute('aria-label')}: ${product.title}`);
    remove.addEventListener('click', () => {
      writeWishlist(readWishlist().filter((h) => h !== product.handle));
    });
    return row;
  }
}

if (!customElements.get('s-wishlist')) customElements.define('s-wishlist', SWishlist);
